"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import * as React from "react";

export interface ImageTransformOptions {
  /**
   * Output width in pixels
   */
  width?: number;
  /**
   * Output height in pixels
   */
  height?: number;
  /**
   * Output format
   */
  format?: "webp" | "avif" | "jpeg" | "png" | "gif";
  /**
   * Quality (1-100)
   */
  quality?: number;
  /**
   * Fit mode for resizing
   */
  fit?: "cover" | "contain" | "fill" | "inside" | "outside";
  /**
   * Background color for transparent images (hex without #)
   */
  background?: string;
  /**
   * Blur amount (0.3-1000)
   */
  blur?: number;
  /**
   * Sharpen amount (0-10)
   */
  sharpen?: number;
  /**
   * Rotation angle (0, 90, 180, 270)
   */
  rotate?: 0 | 90 | 180 | 270;
  /**
   * Flip horizontally
   */
  flipH?: boolean;
  /**
   * Flip vertically
   */
  flipV?: boolean;
}

export interface ImageProps extends Omit<NextImageProps, "loader"> {
  /**
   * Stack0 CDN transformation options
   * These are applied via URL query parameters
   */
  transforms?: ImageTransformOptions;
  /**
   * Custom loader function (overrides default Stack0 loader)
   */
  loader?: NextImageProps["loader"];
}

/**
 * Builds transformation query string for Stack0 CDN
 */
function buildTransformQuery(
  transforms: ImageTransformOptions | undefined,
  loaderWidth?: number,
  loaderQuality?: number,
): string {
  const params = new URLSearchParams();

  // Use loader width/quality if provided, otherwise use transforms
  const width = loaderWidth ?? transforms?.width;
  const quality = loaderQuality ?? transforms?.quality;

  if (width) params.set("w", width.toString());
  if (transforms?.height) params.set("h", transforms.height.toString());
  if (transforms?.format) params.set("f", transforms.format);
  if (quality) params.set("q", quality.toString());
  if (transforms?.fit) params.set("fit", transforms.fit);
  if (transforms?.background) params.set("bg", transforms.background);
  if (transforms?.blur) params.set("blur", transforms.blur.toString());
  if (transforms?.sharpen) params.set("sharpen", transforms.sharpen.toString());
  if (transforms?.rotate) params.set("rotate", transforms.rotate.toString());
  if (transforms?.flipH) params.set("fliph", "1");
  if (transforms?.flipV) params.set("flipv", "1");

  return params.toString();
}

/**
 * Creates a Stack0 CDN image loader
 */
function createStack0Loader(transforms?: ImageTransformOptions) {
  return function stack0Loader({
    src,
    width,
    quality,
  }: {
    src: string;
    width: number;
    quality?: number;
  }): string {
    // If src already has query params, parse and merge
    const url = new URL(src, "https://cdn.stack0.dev");
    const existingParams = url.search;

    // Build transform query
    const transformQuery = buildTransformQuery(transforms, width, quality);

    if (!transformQuery) {
      return src;
    }

    // Merge existing params with transforms
    const separator = existingParams ? "&" : "?";
    const baseSrc = existingParams ? src : src.split("?")[0];

    return existingParams ? `${src}&${transformQuery}` : `${baseSrc}?${transformQuery}`;
  };
}

/**
 * Image component optimized for Stack0 CDN
 *
 * Uses a custom loader that bypasses Next.js image optimization
 * in favor of Stack0's CDN-level image processing.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Image
 *   src="https://cdn.stack0.dev/your-image.jpg"
 *   alt="Description"
 *   width={800}
 *   height={600}
 * />
 *
 * // With CDN transforms
 * <Image
 *   src="https://cdn.stack0.dev/your-image.jpg"
 *   alt="Description"
 *   width={800}
 *   height={600}
 *   transforms={{
 *     format: "webp",
 *     quality: 85,
 *     fit: "cover",
 *   }}
 * />
 * ```
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>(({ transforms, loader, ...props }, ref) => {
  // Use custom loader if provided, otherwise use Stack0 loader
  const imageLoader = loader ?? createStack0Loader(transforms);

  return <NextImage ref={ref} loader={imageLoader} {...props} />;
});

Image.displayName = "Image";

export { createStack0Loader, buildTransformQuery };
